import { useEffect, useMemo, useState } from "react";
import { devError } from "@/lib/log";
import { api, fmt } from "@/lib/api";
import { ShareNetwork, Eye, Sparkle, Trophy, Hash, Tag, MagnifyingGlass, X } from "@phosphor-icons/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");

  const filteredCodes = useMemo(() => {
    const all = data?.top_codigos || [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => `${c.code || ""} ${c.product_nombre || ""}`.toLowerCase().includes(q));
  }, [data, query]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/referrals/stats");
        setData(data);
      } catch (e) {
        devError(e);
      }
    })();
  }, []);

  if (!data)
    return (
      <div className="px-6 md:px-10 py-10 text-sm text-muted2 font-mono">Cargando…</div>
    );

  const fmtDay = (s) => {
    try {
      const d = new Date(s);
      return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
    } catch {
      return s;
    }
  };

  const series = (data.serie_diaria || []).map((d) => ({ ...d, label: fmtDay(d.date) }));

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="referrals-page">
      <div className="mb-8 md:mb-10">
        <div className="overline text-klein">Referidos</div>
        <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-2">
          Cada compartida,
          <br />
          <span className="text-klein">una venta más cerca.</span>
        </h1>
        <p className="text-muted2 mt-3 max-w-xl">
          Tus compradores comparten tus productos a cambio de un descuento. Aquí ves cuánto tráfico generan
          y cuántas ventas convierten.
        </p>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-5 border-t border-l border-zinc-200 mb-10">
        <Kpi label="Códigos generados" value={data.total} testid="kpi-total" />
        <Kpi label="Visitas" value={data.visitas} accent testid="kpi-visitas" sub="referidas" />
        <Kpi label="Códigos usados" value={data.usados} testid="kpi-usados" />
        <Kpi label="Conversión" value={`${data.conversion_pct}%`} testid="kpi-conv" sub="código → venta" />
        <Kpi label="Descuento otorgado" value={fmt(data.total_descuento_otorgado)} positive={false} testid="kpi-desc" sub={`${data.ventas_referidas} ventas`} />
      </section>

      {/* Time-series chart */}
      <section className="border border-zinc-200 p-6 mb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Últimos 30 días</div>
            <div className="h-display text-2xl font-bold mt-1">Visitas y usos por día</div>
          </div>
          <Eye size={22} className="text-klein" />
        </div>
        <div className="h-72">
          {series.every((d) => !d.visitas && !d.usados) ? (
            <Empty message="Aún no hay datos. Comparte tu primer producto para empezar a medir." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#002FA7" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00C853" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00C853" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#52525B" }} interval={3} />
                <YAxis tick={{ fontSize: 11, fill: "#52525B" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 0, border: "1px solid #09090B", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="visitas"
                  stroke="#002FA7"
                  fill="url(#gv)"
                  strokeWidth={2}
                  name="Visitas"
                />
                <Area
                  type="monotone"
                  dataKey="usados"
                  stroke="#00C853"
                  fill="url(#gu)"
                  strokeWidth={2}
                  name="Códigos usados"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Top codes table */}
      <section className="border border-zinc-200">
        <div className="px-6 py-5 border-b border-zinc-200 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-klein" weight="fill" />
            <div>
              <div className="overline">Top códigos</div>
              <div className="h-display text-xl font-bold mt-1">Por descuento generado</div>
            </div>
          </div>
          {(data.top_codigos || []).length > 0 && (
            <div className="relative w-full sm:w-64" data-testid="ref-search-wrap">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" />
              <input
                type="text"
                className="input-flat pl-8 pr-8"
                placeholder="Buscar código o producto…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="ref-search"
                data-help="Encuentra un código de referido o filtra por producto."
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted2 hover:text-ink"
                  data-testid="ref-search-clear"
                  title="Limpiar"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>
        {filteredCodes.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted2">
            <ShareNetwork size={28} className="mx-auto mb-3 text-zinc-400" />
            {query ? `No hay códigos con "${query}".` : "Aún no se ha compartido ningún producto."}
          </div>
        ) : (
          <>
            <div className="overflow-auto hidden lg:block" data-testid="referrals-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-[11px] uppercase tracking-[0.2em] font-medium text-muted2">
                    <th className="text-left px-4 py-3">Código</th>
                    <th className="text-left px-4 py-3">Producto</th>
                    <th className="text-right px-4 py-3 w-20">% off</th>
                    <th className="text-right px-4 py-3 w-20">Visitas</th>
                    <th className="text-right px-4 py-3 w-24">Estado</th>
                    <th className="text-right px-4 py-3 w-32">Descuento</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.map((c) => (
                    <tr
                      key={c.code}
                      className="border-b border-zinc-100 hover:bg-zinc-50"
                      style={{ borderLeft: `3px solid ${c.used ? "#F9A825" : "#00C853"}` }}
                      data-testid={`top-code-${c.code}`}
                    >
                      <td className="px-4 py-3 font-mono">
                        <div className="flex items-center gap-1">
                          <Hash size={12} className="text-muted2" />
                          {c.code}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 truncate max-w-xs">
                          <Tag size={12} className="text-klein" />
                          {c.product_nombre || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.discount_pct}%</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.visits}</td>
                      <td className="px-4 py-3 text-right">
                        {c.used ? (
                          <span className="text-[11px] px-2 py-0.5 bg-[#E8F5E9] text-[#1B5E20] border border-[#C8E6C9]">
                            Usado
                          </span>
                        ) : (
                          <span className="text-[11px] px-2 py-0.5 bg-zinc-100 border border-zinc-200">
                            Activo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-[#FF6B35]">
                        {c.descuento_monto > 0 ? `−${fmt(c.descuento_monto)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden space-y-3 px-3 pb-3" data-testid="referrals-mobile-cards">
              {filteredCodes.map((c) => (
                <div
                  key={c.code}
                  className="border border-zinc-200 p-3"
                  style={{ borderLeft: `3px solid ${c.used ? "#F9A825" : "#00C853"}` }}
                  data-testid={`top-code-card-${c.code}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm font-medium flex items-center gap-1">
                        <Hash size={12} className="text-muted2" /> {c.code}
                      </div>
                      <div className="text-[11px] text-muted2 truncate mt-0.5 flex items-center gap-1">
                        <Tag size={10} className="text-klein shrink-0" />
                        {c.product_nombre || "—"}
                      </div>
                    </div>
                    {c.used ? (
                      <span className="text-[10px] px-2 py-0.5 bg-[#E8F5E9] text-[#1B5E20] border border-[#C8E6C9] shrink-0">
                        Usado
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 bg-zinc-100 border border-zinc-200 shrink-0">
                        Activo
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                    <div>
                      <div className="overline">% off</div>
                      <div className="tabular-nums font-medium">{c.discount_pct}%</div>
                    </div>
                    <div>
                      <div className="overline">Visitas</div>
                      <div className="tabular-nums font-medium">{c.visits}</div>
                    </div>
                    <div>
                      <div className="overline">Descuento</div>
                      <div className="tabular-nums font-medium text-[#FF6B35]">
                        {c.descuento_monto > 0 ? `−${fmt(c.descuento_monto)}` : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* How it works */}
      <section className="mt-10 grid grid-cols-1 md:grid-cols-3 border-t border-l border-zinc-200">
        <Step n="1" title="El comprador comparte" desc="Click en 'Mejor precio garantizado' en la página pública genera un código único de 10 caracteres." />
        <Step n="2" title="Comparte en sus redes" desc="Web Share API nativo en móvil. En desktop copia el link al portapapeles." />
        <Step n="3" title="Desbloquea descuento" desc="Tras compartir, su precio baja automáticamente. Cada código es de un solo uso." />
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, accent, positive, testid }) {
  return (
    <div className="border-r border-b border-zinc-200 p-6" data-testid={testid}>
      <div className="overline">{label}</div>
      <div
        className={`h-display text-3xl lg:text-4xl font-black tracking-tighter mt-3 ${
          accent ? "text-klein" : positive === false ? "text-[#FF6B35]" : ""
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-muted2 mt-2">{sub}</div>}
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="border-r border-b border-zinc-200 p-8">
      <div className="flex items-baseline gap-2">
        <div className="h-display text-4xl font-black text-klein">{n}</div>
        <Sparkle size={14} className="text-klein" weight="fill" />
      </div>
      <div className="h-display text-lg font-bold mt-3">{title}</div>
      <p className="text-sm text-muted2 mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function Empty({ message }) {
  return (
    <div className="h-full w-full flex items-center justify-center text-sm text-muted2 border border-dashed border-zinc-300 p-6">
      {message}
    </div>
  );
}
